/**
 * « Terminés » — les feuilles que l'élève a closes, la plus récente en tête,
 * avec tous leurs corrigés (Hedi, 5 octobre 2026).
 *
 * **Le seul écran de la coquille enfant qui montre un corrigé**, et c'est une
 * exception écrite à la règle 2 : limitée aux feuilles que l'élève a composées
 * **et** closes lui-même, signée côté serveur par
 * `_signer_le_corrige_pour_l_eleve`. Une feuille encore ouverte n'en porte
 * aucun, une feuille du parent non plus — ce que le serveur affirme sur le
 * texte HTTP brut. D'où l'exception nommée dans `formes-papier.test`, qui
 * interdit `corrige_texte` à tout le reste de `child/`.
 *
 * Par exercice : l'énoncé, la photo prise avant la clôture, le corrigé de
 * l'éditeur (images, puis texte quand il y en a), et le verdict du parent **en
 * mots** — sans score ni pourcentage (§14.1). Une ligne que le parent n'a pas
 * encore relue le dit, plutôt que de laisser croire qu'elle est fausse.
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import type { EntrainementOut, LigneDEntrainementOut } from "@/api/schema";
import { partagerMesFeuilles, verdictEnMots } from "@/child/monEntrainement";
import { intituleDExercice } from "@/ui/cahiers";
import { enClair } from "@/ui/dates";
import { Decoupe, pagesDe } from "@/ui/Decoupe";
import { EnonceImages, imagesDeLEnonce } from "@/ui/EnonceImage";
import { parChapitre } from "@/ui/feuille";
import { EtiquetteParascolaire } from "@/ui/Parascolaire";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

type Etat =
  | { quoi: "chargement" }
  | { quoi: "panne" }
  | { quoi: "pret"; closes: EntrainementOut[] };

export function MesEntrainementsTermines() {
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });

  const charger = useCallback(async () => {
    try {
      const reponse = await call("mesPropresEntrainements");
      setEtat({ quoi: "pret", closes: partagerMesFeuilles(reponse.entrainements ?? []).closes });
    } catch {
      setEtat({ quoi: "panne" });
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  if (etat.quoi === "chargement") return <Loading />;

  if (etat.quoi === "panne") {
    return (
      <Screen title="Entraînements terminés">
        <ErrorNote>Je n'arrive pas à lire tes entraînements.</ErrorNote>
        <Button variant="quiet" onClick={() => void charger()}>
          Réessayer
        </Button>
      </Screen>
    );
  }

  return (
    <Screen title="Entraînements terminés">
      {etat.closes.length === 0 ? (
        <Card>
          <Empty>Aucun entraînement clos pour l'instant.</Empty>
        </Card>
      ) : null}
      {etat.closes.map((feuille) => (
        <Feuille key={feuille.id} feuille={feuille} />
      ))}
    </Screen>
  );
}

function Feuille({ feuille }: { feuille: EntrainementOut }) {
  const lignes = feuille.lignes ?? [];
  return (
    <Card title={feuille.closed_at ? `Close le ${enClair(feuille.closed_at)}` : "Close"}>
      {parChapitre(lignes).map((bloc) => (
        <div key={`${bloc.ouvrageTitre}/${bloc.chapitre}`}>
          <p className="devoirs__entete">
            <strong>{bloc.titre}</strong>
            <span className="muted small"> · {bloc.ouvrageTitre}</span>
          </p>
          <ul className="devoirs__liste">
            {bloc.lignes.map((ligne) => (
              <Ligne key={ligne.ligne_id} ligne={ligne} ouvrage={bloc.ouvrageTitre} />
            ))}
          </ul>
        </div>
      ))}
    </Card>
  );
}

function Ligne({ ligne, ouvrage }: { ligne: LigneDEntrainementOut; ouvrage: string }) {
  const intitule = intituleDExercice(ligne);
  const enonces = imagesDeLEnonce(ligne.enonce_urls, ligne.enonce_url);
  const corrige = pagesDe(ligne.corrige_urls, ligne.corrige_url);
  const texte = (ligne.corrige_texte ?? "").trim();

  return (
    <li className="devoirs__ligne">
      <p className="devoirs__entete">
        <strong>{intitule}</strong>
        <span className="muted small"> · {ligne.competence}</span>
      </p>
      <p className="small">{verdictEnMots(ligne)}</p>

      {enonces.length ? (
        <>
          <EtiquetteParascolaire nom={ouvrage} />
          <EnonceImages images={enonces} statement="" />
        </>
      ) : null}

      {ligne.photo_url ? (
        <a href={ligne.photo_url} target="_blank" rel="noreferrer">
          <img
            className="devoirs__photo"
            src={ligne.photo_url}
            alt="Ta réponse photographiée"
            loading="lazy"
          />
        </a>
      ) : (
        <p className="muted small">Pas de photo pour cet exercice.</p>
      )}

      {/* Le corrigé, images puis texte. Ni l'un ni l'autre : on le dit, un
          cadre vide se lisant comme une panne. */}
      <p className="muted small">Le corrigé</p>
      <Decoupe urls={corrige} intitule={intitule} />
      {texte ? <p className="mon-entrainement__corrige">{texte}</p> : null}
      {!corrige.length && !texte ? (
        <p className="muted small">L'éditeur ne donne pas de corrigé pour cet exercice.</p>
      ) : null}
    </li>
  );
}
